import React from 'react';
import { Link } from 'react-router-dom';
import { ShopifyProduct } from '../../types/shopify';
import './ProductCard.scss';

interface ProductCardProps {
  product: ShopifyProduct;
}

const ProductCard: React.FC<ProductCardProps> = ({ product }) => {
  const formatPrice = (amount: string, currencyCode: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parseFloat(amount));
  };

  const getMainImage = () => {
    return product.images && product.images.length > 0 
      ? product.images[0] 
      : null;
  };

  // Get the primary color (first variant's color)
  const getPrimaryColor = (): string | null => {
    const firstVariant = product.variants[0];
    if (!firstVariant) return null;
    
    const colorOption = firstVariant.selectedOptions.find(
      opt => opt.name.toLowerCase() === 'color'
    );
    return colorOption?.value || null;
  };

  // Get unique sizes and their availability
  const getSizeAvailability = (): { size: string; available: boolean }[] => {
    const sizeMap = new Map<string, boolean>();
    
    product.variants.forEach(variant => {
      const sizeOption = variant.selectedOptions.find(
        opt => opt.name.toLowerCase() === 'size'
      );
      if (sizeOption?.value) {
        // If any variant with this size is available, mark it as available
        if (!sizeMap.has(sizeOption.value) || variant.availableForSale) {
          sizeMap.set(sizeOption.value, variant.availableForSale);
        }
      }
    });

    // Sort sizes in order
    const sizeOrder = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
    return Array.from(sizeMap.entries())
      .sort((a, b) => {
        const indexA = sizeOrder.indexOf(a[0]);
        const indexB = sizeOrder.indexOf(b[0]);
        if (indexA === -1 && indexB === -1) return 0;
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      })
      .map(([size, available]) => ({ size, available }));
  };

  const primaryColor = getPrimaryColor();
  const sizes = getSizeAvailability();
  const isAvailable = product.availableForSale && product.variants.some(v => v.availableForSale);

  return (
    <div className="product-card">
      <Link to={`/products/${product.handle}`} className="product-card__link">
        <div className="product-card__image-container">
          {getMainImage() ? (
            <img
              src={getMainImage()!.url}
              alt={product.title}
              className="product-card__image"
              loading="lazy"
            />
          ) : (
            <div className="product-card__image-placeholder" />
          )}
        </div>

        <div className="product-card__info">
          <h3 className="product-card__title">{product.title}</h3>
          
          {/* {primaryColor && (
            <p className="product-card__color">{primaryColor}</p>
          )} */}

          {isAvailable ? (
            <p className="product-card__price">
              {formatPrice(product.priceRange.minVariantPrice.amount, product.priceRange.minVariantPrice.currencyCode)}
            </p>
          ) : (
            <p className="product-card__sold-out">Sold Out</p>
          )}

          {/* {sizes.length > 0 && (
            <div className="product-card__sizes">
              {sizes.map(({ size, available }) => (
                <span 
                  key={size} 
                  className={`product-card__size ${!available ? 'product-card__size--unavailable' : ''}`}
                >
                  {size}
                </span>
              ))}
            </div>
          )} */}
        </div>
      </Link>
    </div>
  );
};

export default ProductCard;
